import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useCart from '../hooks/useCart';
import useOrderStatus from '../hooks/useOrderStatus';
import { formatCurrency } from '../config/api';
import './AppShell.css';

/** Flags the moment the cart count grows, so the badge can react. */
function useCartPulse(count) {
    const [pulse, setPulse] = useState(false);
    const prev = useRef(count);

    useEffect(() => {
        if (count > prev.current) {
            setPulse(true);
            const t = setTimeout(() => setPulse(false), 620);
            prev.current = count;
            return () => clearTimeout(t);
        }
        prev.current = count;
    }, [count]);

    return pulse;
}

const TABS = [
    { to: '/', icon: 'fa-solid fa-house', label: 'Trang chủ', match: (p) => p === '/' },
    { to: '/menu', icon: 'fa-solid fa-cookie-bite', label: 'Menu', match: (p) => p === '/menu' },
    { to: '/gift-box', icon: 'fa-solid fa-gift', label: 'Hộp quà', match: (p) => p === '/gift-box' },
    { to: '/cart', icon: 'fa-solid fa-basket-shopping', label: 'Giỏ hàng', match: (p) => p === '/cart', badge: true },
    { to: '/my-orders', icon: 'fa-regular fa-clipboard', label: 'Đơn của tôi', match: (p) => p === '/my-orders' || p === '/list-bill' },
];

/** Native-style bottom tab bar. */
export function TabBar() {
    const { pathname } = useLocation();
    const { count } = useCart();
    const pulse = useCartPulse(count);

    return (
        <nav className="tabbar" aria-label="Điều hướng chính">
            <div className="tabbar-inner">
                {TABS.map((t) => {
                    const on = t.match(pathname);
                    return (
                        <Link key={t.to} to={t.to} className={`tab ${on ? 'on' : ''}`}>
                            <span className={`tab-icon ${t.badge && pulse ? 'nudge' : ''}`}>
                                <i className={t.icon}></i>
                                {t.badge && count > 0 && (
                                    <span className={`tab-badge ${pulse ? 'bump' : ''}`}>{count > 9 ? '9+' : count}</span>
                                )}
                            </span>
                            <span className="tab-label">{t.label}</span>
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
}

/**
 * Top app bar. Collapses to a compact translucent bar once the page scrolls,
 * the way a native navigation bar does.
 */
export function AppBar({ title, subtitle, back, onBack, right, transparent }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const { orderEnabled, orderStatus } = useOrderStatus();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <header className={`appbar ${scrolled ? 'stuck' : ''} ${transparent && !scrolled ? 'ghost' : ''}`}>
            {/* Store paused top alert */}
            {!orderEnabled && (
                <div className="shop-paused-topstrip">
                    <div className="shop-paused-topstrip-inner">
                        <span className="paused-pulse-dot" style={{ width: 7, height: 7, flexShrink: 0 }} />
                        <span className="shop-paused-topstrip-text">
                            <strong>Tạm ngưng nhận đơn mới:</strong> Tiệm đang chuẩn bị các mẻ bánh. Hotline: {orderStatus?.hotline || '0856.345.789'}
                        </span>
                        <Link to="/cart" className="shop-paused-topstrip-link">Xem chi tiết <i className="fa-solid fa-arrow-right" style={{ fontSize: 10 }} /></Link>
                    </div>
                </div>
            )}

            {/* Utility strip — desktop only */}
            <div className="topstrip">
                <div className="topstrip-inner">
                    <span><i className="fa-solid fa-phone" /> Hotline &amp; Zalo: 0856.345.789</span>
                    <span className="topstrip-dot" />
                    <span><i className="fa-regular fa-clock" /> Đặt trước tối thiểu 48 giờ</span>
                    <span className="topstrip-end">
                        <i className="fa-solid fa-gift" /> Tặng kèm vỏ hộp &amp; túi xách · Thanh toán VietQR
                    </span>
                </div>
            </div>

            <div className={`appbar-inner ${back ? 'has-back' : ''}`}>
                {back && (
                    <button
                        type="button"
                        className="icon-btn appbar-back"
                        aria-label="Quay lại"
                        onClick={() => (onBack ? onBack() : navigate(-1))}
                    >
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>
                )}

                <Link to="/" className="appbar-brand" aria-label="Cookie Shop">
                    <img
                        src={process.env.PUBLIC_URL + '/imgs/logo.png'}
                        alt="Cookie Shop"
                        className="brand-logo-circle"
                    />
                    <span className="appbar-brand-text">
                        <span className="appbar-brand-name">Cookie Shop</span>
                        <span className="appbar-brand-tag">Bánh Trung Thu Handmade</span>
                    </span>
                </Link>

                {/* Phone: the bar carries the screen title. Desktop: the page does. */}
                <div className="appbar-title">
                    <div className="appbar-title-main">{title}</div>
                    {subtitle && <div className="appbar-title-sub">{subtitle}</div>}
                </div>

                {/* Desktop only — on phones this navigation lives in the bottom tab bar */}
                <nav className="appbar-nav">
                    {TABS.filter((t) => !t.badge).map((t) => (
                        <Link key={t.to} to={t.to} className={`appbar-nav-link ${t.match(pathname) ? 'on' : ''}`}>
                            {t.label}
                        </Link>
                    ))}
                </nav>

                <div className="appbar-right">{right}</div>
            </div>
        </header>
    );
}

/** Cart button for the app bar. */
export function CartButton() {
    const navigate = useNavigate();
    const { count, total } = useCart();
    const pulse = useCartPulse(count);

    return (
        <button
            type="button"
            className={`icon-btn cart-btn ${pulse ? 'nudge' : ''}`}
            aria-label="Giỏ hàng"
            onClick={() => navigate('/cart')}
        >
            <i className="fa-solid fa-basket-shopping"></i>
            <span className="cart-btn-text">
                Giỏ hàng
                {total > 0 && <span className="cart-btn-total">{formatCurrency(total)}</span>}
            </span>
            {count > 0 && (
                <span className={`icon-btn-dot ${pulse ? 'bump' : ''}`}>{count > 9 ? '9+' : count}</span>
            )}
        </button>
    );
}

/**
 * Page frame: fixed app bar + scrolling content + tab bar.
 * `actionBar` renders a sticky bottom CTA above the tab bar.
 */
export default function AppShell({
    title,
    subtitle,
    back,
    onBack,
    right,
    transparent,
    tabbar = true,
    actionBar,
    className = '',
    head = true,
    crumbs,
    headAside,
    children,
}) {
    return (
        <>
            <AppBar title={title} subtitle={subtitle} back={back} onBack={onBack} right={right} transparent={transparent} />

            <div className={`page ${actionBar ? 'has-actionbar' : ''} ${tabbar ? '' : 'no-tabbar'} ${className}`}>
                {head && (
                    <div className="page-head shell">
                        <div className="page-head-text">
                            {crumbs && (
                                <nav className="crumbs" aria-label="Đường dẫn">
                                    <Link to="/">Trang chủ</Link>
                                    {crumbs.map((c) => (
                                        <React.Fragment key={c.label}>
                                            <i className="fa-solid fa-chevron-right" />
                                            {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
                                        </React.Fragment>
                                    ))}
                                </nav>
                            )}
                            <h1 className="page-head-title">{title}</h1>
                            {subtitle && <p className="page-head-sub">{subtitle}</p>}
                        </div>
                        {headAside && <div className="page-head-aside">{headAside}</div>}
                    </div>
                )}

                {children}
            </div>

            {actionBar && (
                <div className={`actionbar ${tabbar ? '' : 'floating'}`}>
                    <div className="actionbar-inner">{actionBar}</div>
                </div>
            )}

            {tabbar && <TabBar />}
        </>
    );
}
